/**
 * Bringing a panel that has just opened into view.
 *
 * R-20's return panel was mounted at the FOOT of the page, below the whole inventory table and
 * everything after it. Pressing "Request return of N" put a panel in the accessibility tree and
 * changed nothing inside the viewport, so a retailer read the button as broken and pressed it
 * again. The panel moves up the page as well, but placement alone is not the fix: on a long table
 * the reader can be scrolled past wherever it sits, and then opening it is invisible again.
 *
 * The two decisions that are not DOM work live here so they can be stated and tested:
 * whether a scroll is needed at all, and whether it animates (UI-04's reduced-motion
 * contract — `styles.css` already honours `prefers-reduced-motion` for every transition, and a
 * scripted scroll has to honour it too, because a smooth scroll is motion the user asked not to
 * see).
 */

/** The part of an element's box this needs; `DOMRect` satisfies it. */
export interface Box {
  readonly top: number;
  readonly bottom: number;
}

/**
 * True when any part of the box is outside the viewport.
 *
 * A panel already fully on screen is left alone: scrolling it to the top would move the page
 * under someone who can already see what they opened, which is its own kind of surprise.
 */
export function isOutOfView(box: Box, viewportHeight: number): boolean {
  return box.top < 0 || box.bottom > viewportHeight;
}

/** `smooth` carries the eye from the button to the panel; `auto` when motion is unwelcome. */
export function revealBehavior(prefersReducedMotion: boolean): ScrollBehavior {
  return prefersReducedMotion ? 'auto' : 'smooth';
}

/** Whether this viewer has asked for less motion. False where the query is unavailable. */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
